/**
 * Utility functions to parse itinerary content and extract structured data.
 * Platform-agnostic — no browser/DOM dependencies.
 */

/**
 * Normalize translated itinerary labels back to English so parsing regexes work
 * on itineraries translated to French, Spanish, German, Italian, etc.
 */
export const normalizeContent = (content) => {
  if (!content) return content;
  let s = content;

  // Normalize coordinate labels: "Coordonnées :" → "Coordinates:"
  s = s.replace(/Coordonn[eé]+s\s*:/gi, 'Coordinates:');
  s = s.replace(/Coordenadas\s*:/gi, 'Coordinates:');
  s = s.replace(/Koordinaten\s*:/gi, 'Coordinates:');
  s = s.replace(/Coordinate\s*:/gi, 'Coordinates:');
  s = s.replace(/Co[öo]rdinaten\s*:/gi, 'Coordinates:');
  s = s.replace(/Координаты\s*:/gi, 'Coordinates:');
  s = s.replace(/Koordinatlar\s*:/gi, 'Coordinates:');
  s = s.replace(/座標\s*:/g, 'Coordinates:');
  s = s.replace(/坐标\s*:/g, 'Coordinates:');
  s = s.replace(/좌표\s*:/g, 'Coordinates:');
  s = s.replace(/निर्देशांक\s*:/g, 'Coordinates:');
  s = s.replace(/إحداثيات\s*:/g, 'Coordinates:');
  s = s.replace(/Współrzędne\s*:/gi, 'Coordinates:');

  // Normalize day headers: "**Jour 1:" → "**Day 1:", etc.
  const dayWordPattern = /(\*{0,2}\s*)(?:Jour|D[ií]a|Tag|Giorno|День|Dag|Dzie[ńn]|G[üu]n|يوم|दिन|일차?)(\s+\d)/gi;
  s = s.replace(dayWordPattern, '$1Day$2');

  // Also handle markdown header formats: "## Jour 1" → "## Day 1"
  const mdDayPattern = /(#{1,3}\s*)(?:Jour|D[ií]a|Tag|Giorno|День|Dag|Dzie[ńn]|G[üu]n|يوم|दिन|일차?)(\s+\d)/gi;
  s = s.replace(mdDayPattern, '$1Day$2');

  return s;
};

/**
 * Pattern to detect accommodation-related locations
 */
const ACCOMMODATION_PATTERN = /\bhotel\b|\bresort\b|\bmotel\b|\bhostel\b|\bairbnb\b|\bb&b\b|\bbed\s*&?\s*breakfast\b|\bguesthouse\b|\bguest\s*house\b|\bglamping\b|\bvilla\b|\bchalet\b|\bresidence\b|\bresidency\b|\bpenthouse\b|\baccomodation\b|\baccommodation\b|\briad\b|\bryokan\b|\bpension\b|\bposada\b|\bpousada\b|\bhacienda\b|\bmanor\b|\bchateau\b|\bcastle\s*hotel\b/i;

/**
 * Pattern to detect meal-related locations (should NOT be marked as accommodation)
 */
const MEAL_PATTERN = /\b(breakfast|lunch|dinner|brunch|meal|restaurant|cafe|bistro|eatery|diner|food|dining)\b/i;

/**
 * Check if a location name is an accommodation
 * Returns false if the name contains meal-related words (e.g., "Lunch at Hotel XYZ")
 */
export const isAccommodation = (name) => {
  if (!name) return false;
  // If it contains meal words, it's a meal stop, not accommodation
  if (MEAL_PATTERN.test(name)) return false;
  return ACCOMMODATION_PATTERN.test(name);
};

/**
 * Extract booking link from text
 */
const extractBookingLink = (text) => {
  if (!text) return null;
  const bookingMatch = text.match(/\[Book[^\]]*\]\(([^)]+)\)/i);
  if (bookingMatch) return bookingMatch[1];
  const urlMatch = text.match(/(https?:\/\/www\.booking\.com[^\s\)]*)/i);
  if (urlMatch) return urlMatch[1];
  return null;
};

/**
 * Extract star rating from text
 */
const extractStarRating = (text) => {
  if (!text) return null;
  const starMatch = text.match(/(\d)\s*[-]?\s*star/i);
  if (starMatch) return parseInt(starMatch[1]);
  const starEmojis = (text.match(/⭐|★/g) || []).length;
  if (starEmojis > 0) return starEmojis;
  const slashMatch = text.match(/\((\d(?:\.\d)?)\s*\/\s*5\)/);
  if (slashMatch) return Math.round(parseFloat(slashMatch[1]));
  return null;
};

/**
 * Extract locations with coordinates from itinerary text.
 */
export const extractLocationsWithCoordinates = (content) => {
  if (!content) return [];

  const normalized = normalizeContent(content);
  const locations = [];
  const lines = normalized.split('\n');

  const newFormatPattern = /\*\*([^*]+)\*\*\s*(?:\((?!\d(?:\.\d)?\s*\/\s*5)[^)]*\)\s*)*(?:(?:\((\d(?:\.\d)?)\s*\/\s*5\)\s*))?Coordinates:\s*\((-?\d{1,3}\.?\d*),\s*(-?\d{1,3}\.?\d*)\)\s*[-–—]?\s*(.*)/gi;
  const legacyPattern = /(?:\*\*([^*]+)\*\*|([A-Z][^(C][^(]*?))\s*\((-?\d{1,3}\.?\d*),\s*(-?\d{1,3}\.?\d*)\)\s*[-–—]?\s*(.*)/gi;
  const ratingFormatPattern = /^\s*([A-Z][^(]*(?:\([^)0-9]+\))?[^(]*?)\s*\((\d(?:\.\d)?)\s*\/\s*5\)\s*\((-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)\)/i;
  // Degree notation: **Name:** (22.4833° N, 73.5167° E) or (22.4833°N, 73.5167°E)
  const degreePattern = /\*\*([^*]+)\*\*[^(]*\((\d{1,3}\.?\d*)\s*°?\s*([NSns])[\s,]+(\d{1,3}\.?\d*)\s*°?\s*([EWew])\)[,\s]*(.*)/;

  const parseDegreeCoord = (val, dir) => {
    const n = parseFloat(val);
    return (dir === 'S' || dir === 's') ? -n : (dir === 'W' || dir === 'w') ? -n : n;
  };

  const cleanDescription = (desc) => {
    if (!desc) return '';
    const cleaned = desc
      .replace(/^\s*[-–—:]\s*/, '')
      .replace(/\*\*/g, '')
      .replace(/\[([^\]]*)\]\([^)]+\)/g, '')
      .replace(/https?:\/\/[^\s]+/g, '')
      .replace(/⭐+|★+/g, '')
      .replace(/\d+-star/gi, '')
      .replace(/More\s*Info/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    const logisticsPatterns = [
      /^travel[:\s]/i, /^start\s*point/i, /^end\s*point/i,
      /^walking/i, /^driving/i,
      /^take\s+(the\s+)?(bus|train|subway|metro|taxi|uber)/i,
      /^from\s+(here|accommodation|hotel)/i,
      /^approximately\s+\d+\s*(min|minute|hour|km|mile)/i,
      /^\d+\s*(min|minute|hour|km|mile)/i,
      /^duration[:\s]/i, /^distance[:\s]/i, /^transit[:\s]/i, /^transfer/i,
    ];

    for (const pattern of logisticsPatterns) {
      if (pattern.test(cleaned)) return '';
    }
    return cleaned;
  };

  lines.forEach((line, lineIndex) => {
    let match;

    newFormatPattern.lastIndex = 0;
    while ((match = newFormatPattern.exec(line)) !== null) {
      const name = (match[1] || '').trim();
      const inlineRating = match[2] ? Math.round(parseFloat(match[2])) : null;
      const lat = parseFloat(match[3]);
      const lng = parseFloat(match[4]);
      const rawText = match[5] || '';

      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && name) {
        const cleanName = name.replace(/^[-*•]\s*/, '').replace(/^\d+\.\s*/, '').replace(/:\s*$/, '').trim();
        if (cleanName && cleanName.toLowerCase() !== 'coordinates') {
          let isAccomm = isAccommodation(cleanName);
          const starRating = inlineRating || extractStarRating(rawText);
          const bookingLink = extractBookingLink(rawText);
          if (starRating || bookingLink) isAccomm = true;
          locations.push({
            name: cleanName, lat, lng, line: lineIndex,
            description: cleanDescription(rawText),
            isAccommodation: isAccomm, bookingLink, starRating
          });
        }
      }
    }

    if (locations.filter(l => l.line === lineIndex).length === 0) {
      legacyPattern.lastIndex = 0;
      while ((match = legacyPattern.exec(line)) !== null) {
        const name = (match[1] || match[2] || '').trim();
        const lat = parseFloat(match[3]);
        const lng = parseFloat(match[4]);
        const rawText = match[5] || '';

        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && name) {
          const cleanName = name.replace(/^[-*•]\s*/, '').replace(/^\d+\.\s*/, '').replace(/:\s*$/, '').trim();
          if (cleanName && cleanName.toLowerCase() !== 'coordinates') {
            let isAccomm = isAccommodation(cleanName);
            const starRating = extractStarRating(rawText);
            const bookingLink = extractBookingLink(rawText);
            if (starRating || bookingLink) isAccomm = true;
            locations.push({
              name: cleanName, lat, lng, line: lineIndex,
              description: cleanDescription(rawText),
              isAccommodation: isAccomm, bookingLink, starRating
            });
          }
        }
      }
    }

    if (locations.filter(l => l.line === lineIndex).length === 0) {
      match = line.match(ratingFormatPattern);
      if (match) {
        const name = (match[1] || '').trim();
        const rating = Math.round(parseFloat(match[2]));
        const lat = parseFloat(match[3]);
        const lng = parseFloat(match[4]);

        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && name) {
          const cleanName = name.replace(/^[-*•]\s*/, '').replace(/^\d+\.\s*/, '').replace(/:\s*$/, '').trim();
          if (cleanName && cleanName.toLowerCase() !== 'coordinates') {
            locations.push({
              name: cleanName, lat, lng, line: lineIndex,
              description: '', isAccommodation: isAccommodation(cleanName),
              bookingLink: null, starRating: rating
            });
          }
        }
      }
    }

    // Try degree notation: **Name:** (22.4833° N, 73.5167° E)
    if (locations.filter(l => l.line === lineIndex).length === 0) {
      match = line.match(degreePattern);
      if (match) {
        const rawName = (match[1] || '').trim();
        const lat = parseDegreeCoord(match[2], match[3].toUpperCase());
        const lng = parseDegreeCoord(match[4], match[5].toUpperCase());
        const rawText = match[6] || '';

        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && rawName) {
          const cleanName = rawName.replace(/^[-*•]\s*/, '').replace(/^\d+\.\s*/, '').replace(/:\s*$/, '').trim();
          if (cleanName && cleanName.toLowerCase() !== 'coordinates') {
            locations.push({
              name: cleanName, lat, lng, line: lineIndex,
              description: cleanDescription(rawText),
              isAccommodation: isAccommodation(cleanName),
              bookingLink: extractBookingLink(line), starRating: null
            });
          }
        }
      }
    }
  });

  // Second pass: descriptions and booking links on following lines
  locations.forEach((loc) => {
    if (loc.line < lines.length - 1) {
      let descLines = [];
      for (let i = loc.line + 1; i < Math.min(loc.line + 5, lines.length); i++) {
        const nextLine = lines[i].trim();
        if (!nextLine || /^\*\*[^*]+\*\*/.test(nextLine) || /^#{1,3}\s/.test(nextLine) || /^(?:Day|Part)\s+\d+/i.test(nextLine)) break;
        if (/^[-*•]\s*\*\*/.test(nextLine)) break;

        if (!loc.bookingLink) {
          const bookingLink = extractBookingLink(nextLine);
          if (bookingLink) { loc.bookingLink = bookingLink; loc.isAccommodation = true; }
        }
        if (!loc.starRating) {
          const starRating = extractStarRating(nextLine);
          if (starRating) { loc.starRating = starRating; loc.isAccommodation = true; }
        }
        if (/^\[?More\s*Info\]?/i.test(nextLine)) continue;

        if (!loc.description || loc.description.length < 20) {
          const cleanLine = nextLine.replace(/^[-*•]\s*/, '').replace(/\*\*/g, '').trim();
          const filteredLine = cleanDescription(cleanLine);
          const minLength = loc.isAccommodation ? 10 : 15;
          if (filteredLine && filteredLine.length > minLength && !filteredLine.startsWith('[')) {
            descLines.push(filteredLine);
            if (descLines.length >= 2) break;
          }
        }
      }
      if (descLines.length > 0 && (!loc.description || loc.description.length < 20)) {
        loc.description = descLines.join(' ');
      }
    }
  });

  return locations;
};

/**
 * Group locations by day in the itinerary.
 */
export const extractLocationsByDay = (content) => {
  if (!content) return [];

  const normalized = normalizeContent(content);
  const lines = normalized.split('\n');
  const days = [];
  let currentDay = null;
  let currentDayTitle = '';
  let currentLocations = [];
  let seenLocations = new Set();

  const dayPattern = /^(?:#{1,3}\s*)?\*?\*?(?:Day|Part)\s*(\d+)[:\s\-,]*(.*?)(?:\*\*)?$/i;
  const numberedStopPattern = /^\s*(\d+)\.\s+\*\*([^*]+)\*\*\s*(?:\([^)]*\)\s*)*(?:Coordinates:\s*)?\((-?\d{1,2}\.\d+),\s*(-?\d{1,3}\.\d+)\)/;
  const boldWithCoordsPattern = /\*\*([^*]+)\*\*\s*(?:\([^)]*\)\s*)*(?:Coordinates:\s*)?\((-?\d{1,2}\.\d+),\s*(-?\d{1,3}\.\d+)\)/;
  const ratingFormatPattern = /^\s*([A-Z][^(]*(?:\([^)0-9]+\))?[^(]*?)\s*\((\d(?:\.\d)?)\s*\/\s*5\)\s*(?:Coordinates:\s*)?\((-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)\)/;

  let foundDayHeaders = false;
  let fallbackLocations = [];
  let fallbackSeen = new Set();

  const cleanName = (name) => {
    return name.replace(/\s*\([A-Z]{2,4}\)\s*$/, '').replace(/\(.*$/, '').replace(/:\s*$/, '').trim();
  };

  const addLocation = (name, lat, lng, lineIndex, rawLine) => {
    const cleaned = cleanName(name);
    if (!cleaned || cleaned.length < 2) return;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return;

    let starRating = extractStarRating(rawLine);
    let bookingLink = extractBookingLink(rawLine);
    let isAccomm = isAccommodation(cleaned);

    for (let i = lineIndex + 1; i < Math.min(lineIndex + 4, lines.length); i++) {
      const nextLine = lines[i]?.trim() || '';
      if (/^\*\*[^*]+\*\*/.test(nextLine) || /^#{1,3}\s/.test(nextLine) || /^(?:Day|Part)\s+\d+/i.test(nextLine)) break;
      if (/^\d+\.\s+\*\*/.test(nextLine)) break;
      if (!starRating) starRating = extractStarRating(nextLine);
      if (!bookingLink) bookingLink = extractBookingLink(nextLine);
    }

    if (bookingLink || (starRating && isAccomm)) isAccomm = true;

    const location = { name: cleaned, lat, lng, line: lineIndex, starRating, bookingLink, isAccommodation: isAccomm };

    if (currentDay !== null && !seenLocations.has(cleaned.toLowerCase())) {
      seenLocations.add(cleaned.toLowerCase());
      currentLocations.push(location);
    }
    if (!fallbackSeen.has(cleaned.toLowerCase())) {
      fallbackSeen.add(cleaned.toLowerCase());
      fallbackLocations.push(location);
    }
  };

  const sectionStopPattern = /^#{1,3}\s+(?:Other\s+Points|Checklist|Packing|Additional|Notes|Tips|Summary|Budget|Conclusion)/i;

  lines.forEach((line, lineIndex) => {
    // Stop collecting locations when we hit non-day sections
    if (sectionStopPattern.test(line.trim())) {
      if (currentDay !== null && currentLocations.length > 0) {
        days.push({ day: currentDay, dayTitle: currentDayTitle, locations: currentLocations });
      }
      currentDay = null;
      currentLocations = [];
      seenLocations = new Set();
      return;
    }

    // Skip lines with coordinates — they are location entries, not day headers
    const hasCoordinates = /Coordinates:|\(-?\d{1,3}\.\d+,\s*-?\d{1,3}\.\d+\)/.test(line);

    let dayMatch = !hasCoordinates ? line.match(dayPattern) : null;
    // Also check for day header embedded mid-line (e.g. "...text:**Day 4: ...")
    if (!dayMatch && !hasCoordinates) {
      const midLineMatch = line.match(/\*\*(?:Day|Part)\s*(\d+)[:\s\-,]*(.*?)(?:\*\*)?$/i);
      if (midLineMatch) {
        dayMatch = midLineMatch;
      }
    }
    if (dayMatch) {
      foundDayHeaders = true;
      if (currentDay !== null && currentLocations.length > 0) {
        days.push({ day: currentDay, dayTitle: currentDayTitle, locations: currentLocations });
      }
      currentDay = parseInt(dayMatch[1], 10);
      currentDayTitle = dayMatch[2]?.trim().replace(/\*\*$/, '').trim() || `Day ${currentDay}`;
      currentLocations = [];
      seenLocations = new Set();
      return;
    }

    if (currentDay === null && foundDayHeaders) return;

    let match = line.match(numberedStopPattern);
    if (match) { addLocation(match[2], parseFloat(match[3]), parseFloat(match[4]), lineIndex, line); return; }

    match = line.match(boldWithCoordsPattern);
    if (match) { addLocation(match[1], parseFloat(match[2]), parseFloat(match[3]), lineIndex, line); return; }

    match = line.match(ratingFormatPattern);
    if (match) {
      const name = match[1].trim();
      const rating = Math.round(parseFloat(match[2]));
      const lat = parseFloat(match[3]);
      const lng = parseFloat(match[4]);
      const cleaned = cleanName(name);
      if (cleaned && cleaned.length >= 2 && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        const location = { name: cleaned, lat, lng, line: lineIndex, starRating: rating, bookingLink: extractBookingLink(line), isAccommodation: isAccommodation(cleaned) };
        if (currentDay !== null && !seenLocations.has(cleaned.toLowerCase())) {
          seenLocations.add(cleaned.toLowerCase());
          currentLocations.push(location);
        }
        if (!fallbackSeen.has(cleaned.toLowerCase())) {
          fallbackSeen.add(cleaned.toLowerCase());
          fallbackLocations.push(location);
        }
      }
    }
  });

  if (currentDay !== null && currentLocations.length > 0) {
    days.push({ day: currentDay, dayTitle: currentDayTitle, locations: currentLocations });
  }

  if (!foundDayHeaders && fallbackLocations.length > 0) {
    return [{ day: 1, dayTitle: 'Trip Stops', locations: fallbackLocations }];
  }

  return days;
};

/**
 * Calculate straight-line distance between two coordinates using Haversine formula.
 */
export const calculateDistance = (coord1, coord2) => {
  const R = 6371;
  const dLat = toRad(coord2.lat - coord1.lat);
  const dLng = toRad(coord2.lng - coord1.lng);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(coord1.lat)) * Math.cos(toRad(coord2.lat)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRad = (deg) => deg * (Math.PI / 180);

/**
 * Get a summary of the route for a day.
 */
export const getDayRouteSummary = (locations) => {
  if (!locations || locations.length < 2) {
    return { totalLocations: locations?.length || 0, estimatedDistance: 0, estimatedDuration: 0 };
  }
  let totalDistance = 0;
  for (let i = 0; i < locations.length - 1; i++) {
    totalDistance += calculateDistance(locations[i], locations[i + 1]);
  }
  const roadDistance = totalDistance * 1.3;
  const estimatedMinutes = (roadDistance / 50) * 60;
  return {
    totalLocations: locations.length,
    estimatedDistance: Math.round(roadDistance * 10) / 10,
    estimatedDuration: Math.round(estimatedMinutes)
  };
};

/**
 * Extract the location name from a day title.
 */
export const extractDayLocation = (dayTitle) => {
  if (!dayTitle) return 'Various Locations';
  const match = dayTitle.match(/:\s*(.+)/);
  return match ? match[1].trim() : 'Various Locations';
};

/**
 * #2 Structured output: convert the authoritative server-parsed itinerary
 * document into the exact {day, dayTitle, locations:[...]} shape that
 * extractLocationsByDay produces, so the UI consumes typed data with no
 * rendering changes. Returns null when there is no usable structured doc.
 */
export const structuredToDays = (structured) => {
  if (!structured || !Array.isArray(structured.days) || structured.days.length === 0) return null;
  const days = structured.days
    .filter((d) => d && Array.isArray(d.stops))
    .map((d) => ({
      day: d.day,
      dayTitle: d.title || `Day ${d.day}`,
      travelDistanceKm: typeof d.travel_distance_km === 'number' ? d.travel_distance_km : null,
      travelMinutes: typeof d.travel_minutes === 'number' ? d.travel_minutes : null,
      locations: d.stops
        .filter((s) => s && typeof s.lat === 'number' && typeof s.lng === 'number')
        .map((s) => ({
          name: s.name,
          lat: s.lat,
          lng: s.lng,
          starRating: s.star_rating != null ? s.star_rating : null,
          bookingLink: s.booking_link || null,
          isAccommodation: !!s.is_accommodation,
          description: s.description || '',
          source: s.source || 'ai',
        })),
    }))
    .filter((d) => d.locations.length > 0);
  return days.length > 0 ? days : null;
};

/**
 * Prefer the authoritative server-parsed structured itinerary (#2); fall back
 * to client-side Markdown parsing for legacy trips that predate it. Accepts
 * either a full itinerary object (with .structured_itinerary and .itinerary)
 * or a raw Markdown string.
 */
export const daysFromItinerary = (itineraryObj) => {
  if (itineraryObj && typeof itineraryObj === 'object') {
    const structured = structuredToDays(itineraryObj.structured_itinerary);
    if (structured) return structured;
    if (typeof itineraryObj.itinerary === 'string') return extractLocationsByDay(itineraryObj.itinerary);
    return [];
  }
  if (typeof itineraryObj === 'string') return extractLocationsByDay(itineraryObj);
  return [];
};

/**
 * Structured checklist (server-side) or null if absent.
 */
export const checklistFromItinerary = (itineraryObj) => {
  const structured = itineraryObj && typeof itineraryObj === 'object' ? itineraryObj.structured_itinerary : null;
  if (structured && Array.isArray(structured.checklist) && structured.checklist.length > 0) {
    return structured.checklist.map((c) => ({ label: c.label, done: !!c.done }));
  }
  return null;
};
